import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxAddress, OnyxPhone } from '@onyx/angular';
import {
  ActionHelper,
  AddressHelper,
  OnyxAddressComponent,
  OnyxChipsComponent,
  OnyxDropdownDirective,
  OnyxIconButtonComponent,
  OnyxInformationHeadingComponent,
  OnyxInformationListComponent,
  OnyxInformationRowComponent,
  OnyxMapComponent,
  OnyxMapEvent,
  OnyxMapMarkerType,
  OnyxModalComponent,
  OnyxPhonePipe,
  OnyxTableComponent,
  OnyxToastService,
  OnyxTooltipDirective,
  PhoneHelper,
} from '@onyx/angular';
import { isString } from 'lodash';
import { catchError, EMPTY, Subject, switchMap, tap } from 'rxjs';
import { AuthStore } from '../../../../../../auth/common/stores/auth.store';
import { SHORT_PAGINATION } from '../../../../../../common/constants/common/short-pagination';
import { CommonHelper } from '../../../../../../common/helpers/common.helper';
import { BusinessHoursPipe } from '../../../../../../common/pipes/business-hours.pipe';
import { ModalsStore } from '../../../../../../common/stores/modals.store';
import { DriverHelper } from '../../../../../drivers/common/helpers/driver.helper';
import type { SimplifiedDriver } from '../../../../../drivers/common/interfaces/driver';
import { FleetHelper } from '../../../../../fleet/common/helpers/fleet.helper';
import type { SimplifiedFleet } from '../../../../../fleet/common/interfaces/fleet';
import { BASE_ASSIGNED_DRIVERS_LIST_COLUMNS } from '../../constants/base-assigned-drivers-list-columns';
import { BASE_ASSIGNED_DRIVERS_NOT_FOUND } from '../../constants/base-assigned-drivers-not-found';
import { BASE_ASSIGNED_VEHICLES_LIST_COLUMNS } from '../../constants/base-assigned-vehicles-list-columns';
import { BASE_ASSIGNED_VEHICLES_NOT_FOUND } from '../../constants/base-assigned-vehicles-not-found';
import { getBaseSections } from '../../constants/base-sections';
import { BaseSection } from '../../enums/base-section';
import { BASE_HEADER_ACTIONS, BaseHelper } from '../../helpers/base.helper';
import type { Base, SimplifiedBase } from '../../interfaces/base';
import { BasesService } from '../../services/bases.service';

export type BaseModalData = Base | SimplifiedBase | string;

@Component({
  selector: 'app-base-modal',
  imports: [
    OnyxModalComponent,
    OnyxIconButtonComponent,
    OnyxMapComponent,
    OnyxInformationRowComponent,
    TranslatePipe,
    OnyxPhonePipe,
    OnyxChipsComponent,
    OnyxDropdownDirective,
    OnyxTableComponent,
    OnyxAddressComponent,
    OnyxTooltipDirective,
    BusinessHoursPipe,
    OnyxInformationHeadingComponent,
    OnyxInformationListComponent,
  ],
  templateUrl: './base-modal.component.html',
  styleUrl: './base-modal.component.scss',
})
export class BaseModalComponent {
  protected readonly baseData = inject<BaseModalData>(DIALOG_DATA);
  protected readonly authStore = inject(AuthStore);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(OnyxToastService);
  private readonly basesService = inject(BasesService);
  private readonly injector = inject(Injector);
  private readonly fleetHelper = inject(FleetHelper);
  private readonly driverHelper = inject(DriverHelper);
  private readonly baseHelper = inject(BaseHelper);
  private readonly modalsStore = inject(ModalsStore);

  protected readonly I18N = 'pointsOfInterest';
  protected readonly BASE_ASSIGNED_VEHICLES_COLUMNS =
    BASE_ASSIGNED_VEHICLES_LIST_COLUMNS;
  protected readonly BASE_ASSIGNED_DRIVERS_COLUMNS =
    BASE_ASSIGNED_DRIVERS_LIST_COLUMNS;
  protected readonly BASE_ASSIGNED_DRIVERS_NOT_FOUND =
    BASE_ASSIGNED_DRIVERS_NOT_FOUND;
  protected readonly BASE_ASSIGNED_VEHICLES_NOT_FOUND =
    BASE_ASSIGNED_VEHICLES_NOT_FOUND;

  protected readonly ActionHelper = ActionHelper;
  protected readonly AddressHelper = AddressHelper;
  protected readonly PhoneHelper = PhoneHelper;
  protected readonly BaseSection = BaseSection;

  private readonly map = viewChild.required(OnyxMapComponent);
  private readonly uuid: string;

  protected base = signal<Base | null>(null);
  protected section = this.modalsStore.getActiveSection(
    BaseModalComponent.name,
    () => this.sections(),
  );
  protected pagination = signal(SHORT_PAGINATION);
  protected loading = signal(false);
  protected error = signal(false);
  protected base$ = new Subject<void>();
  protected close$ = new Subject<void>();

  protected assignedVehicles = computed(() =>
    CommonHelper.getPaginatedData(
      this.base()?.assignedVehicles,
      this.pagination(),
    ),
  );
  protected assignedDrivers = computed(() =>
    CommonHelper.getPaginatedData(
      this.base()?.assignedDrivers,
      this.pagination(),
    ),
  );
  protected options = computed(() =>
    this.base()
      ? this.baseHelper.getOptions(
          this.base()!,
          { close$: this.close$ },
          BASE_HEADER_ACTIONS,
        )
      : null,
  );
  protected sections = computed(() => getBaseSections(this.base()));

  constructor() {
    this.uuid = isString(this.baseData) ? this.baseData : this.baseData.uuid;

    this.base$
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(() =>
          this.basesService.getBase(this.uuid).pipe(
            catchError(() => {
              this.base.set(null);
              this.loading.set(false);
              this.error.set(true);

              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((base) => {
        this.base.set(base);
        this.loading.set(false);
        this.error.set(false);
      });

    if (isString(this.baseData) || !BaseHelper.isBaseType(this.baseData)) {
      this.base$.next();
    } else {
      this.base.set(this.baseData);
    }

    this.basesService.reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.base$.next());

    effect(() => {
      const base = this.base();
      if (!base) return;

      afterNextRender(
        () => {
          this.map().dispatch(
            new OnyxMapEvent.AddUpdateMarker({
              id: this.uuid,
              type: OnyxMapMarkerType.POINT_OF_INTEREST,
              coordinates: base.address.coordinates,
            }),
          );
          this.map().dispatch(new OnyxMapEvent.FitContent());
        },
        { injector: this.injector },
      );
    });
  }

  protected edit(): void {
    this.baseHelper.edit(this.uuid, { close$: this.close$ });
  }

  protected copyAddress(address: OnyxAddress): void {
    ActionHelper.copy(AddressHelper.composeLabel(address), this.toastService);
  }

  protected copyCoordinates(address: OnyxAddress): void {
    ActionHelper.copy(
      AddressHelper.composeCoordinatesLabel(address),
      this.toastService,
    );
  }

  protected copyPhone(phone: OnyxPhone): void {
    ActionHelper.copy(PhoneHelper.composeLabel(phone), this.toastService);
  }

  protected openDriverModal(driver: SimplifiedDriver): void {
    this.driverHelper.openModal(driver);
  }

  protected openVehicleModal(vehicle: SimplifiedFleet): void {
    this.fleetHelper.openModal(vehicle);
  }
}
